import React, { useCallback, useEffect, useState } from "react";
import { Modal, Typography, InputNumber } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import api from "../../../services/api";
import { useToast } from "../../../hooks/toast";
import { IRequirement } from "../../Project";

const { Text } = Typography;

interface IConcludeActivityModalProps {
  activity?: IRequirement;
  visible: boolean;
  handleClose: (activity?: IRequirement) => void;
}

const ConcludeActivityModal: React.FC<IConcludeActivityModalProps> = ({
  activity,
  visible,
  handleClose,
}) => {
  const { t } = useTranslation(["activities-actions"]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hours, setHours] = useState<number | undefined>(undefined);

  const { addToast } = useToast();

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 405:
          description = "O projeto não foi encontrado";
          break;
        case 404:
          description = "A atividade não foi encontrada";
          break;
        case 403:
          description =
            "A atividade só pode ser concluida pelo usuário atribuído";
          break;
        default:
          description =
            "Um erro occoreu ao concluir a atividade, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao concluir",
        description,
      });
    },
    [addToast],
  );

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.patch(`/activities/${activity?.id}/conclude`, {
        hours,
      });

      handleClose(response.data);

      addToast({
        type: "success",
        title: "Atividade concluída",
        description: "A atividade foi concluída com sucesso",
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError;
        generateErrorToast(serverError.response);
      } else {
        console.log(err);
      }
    }

    setLoading(false);
  }, [activity?.id, addToast, hours, generateErrorToast, handleClose]);

  return (
    <Modal
      title={t("conclude")}
      visible={visible}
      onOk={handleSubmit}
      onCancel={() => handleClose()}
      destroyOnClose
      okButtonProps={{
        loading,
        disabled: !hours,
      }}
      getContainer={false}
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Text style={{ marginBottom: 10 }}>
        {`Qual foi o tempo nécessário para concluir a atividade "${activity?.description}"?`}
      </Text>
      <InputNumber
        style={{ width: 200 }}
        onChange={setHours}
        defaultValue={activity?.wasted_hours}
        min={1}
        max={400}
        addonAfter="horas"
      />
    </Modal>
  );
};

export default ConcludeActivityModal;
